import { useAppSelector } from "integrations/redux/hooks"
import { getSidePanelPdfPlugins } from "plugins/plugin-registry"
import { compareFunctionsBySortMode } from "./constants"
import { selectFilterPDFsValue, selectSearchPDFsValue, selectSortPDFsValue } from "./slice.new"
import { selectEntitiesFromIds } from "./slice"
import { useMemo } from "react"
import { omit } from "nodash"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"

export type SidePanelPdfsData = {
    id: string
    index: string
    parentId?: string
    children: SidePanelPdfsData[]
    plugin: ReturnType<typeof getSidePanelPdfPlugins>[number]
}

export type FlattenedSidePanelPdfsData = Omit<SidePanelPdfsData, "children"> & {
    depth: number
    isLastItemInParent: boolean
    directChildrenCount: number
    ancestors: { id: string; isLastItemInParent: boolean }[]
}

const usePdfSidePanelData = () => {
    const sidePanelPlugins = getSidePanelPdfPlugins()
    const data: Record<string, SidePanelPdfsData> = sidePanelPlugins.reduce(
        (acc, cur) => {
            const entities = cur.useSidePanelEntities()
            entities.forEach((entity) => {
                acc[entity.id] = { ...entity, children: [], plugin: cur }
            })
            return acc
        },
        {} as Record<string, SidePanelPdfsData>
    )
    const roots: SidePanelPdfsData[] = []
    Object.values(data).forEach((entity) => {
        if (entity.parentId && entity.parentId !== "null") {
            const parent = data[entity.parentId]
            if (parent) {
                parent.children.push(entity)
            }
        } else {
            roots.push(entity)
        }
    })
    return roots
}

const usePdfSidePanelEntityMap = () => {
    const sidePanelData = usePdfSidePanelData()
    const entities = useAppSelector(
        selectEntitiesFromIds(
            sidePanelData.reduce((acc, cur) => {
                const pushNestedIds = (data: SidePanelPdfsData[]) => {
                    data.forEach((d) => {
                        acc.push(d.id)
                        pushNestedIds(d.children)
                    })
                }
                pushNestedIds([cur])
                return acc
            }, [] as string[])
        )
    )
    const entityMap = useMemo(() => {
        return Object.fromEntries(entities.map((entity) => [entity.id, entity]))
    }, [entities])
    return entityMap
}

export const useFilteredAndSortedPdfSidePanelData = () => {
    const sidePanelData = usePdfSidePanelData()
    const searchPdfValue = useAppSelector(selectSearchPDFsValue)
    const filterPdfValue = useAppSelector(selectFilterPDFsValue)
    const sortPdfValue = useAppSelector(selectSortPDFsValue)
    const entityMap = usePdfSidePanelEntityMap()
    const filterList = (list: SidePanelPdfsData[]) => {
        return list
            .map((data): SidePanelPdfsData | null => {
                const entity = entityMap[data.id]
                const filteredChildren = data.children ? filterList(data.children) : []
                const matchesSearch = entity.name
                    .toLowerCase()
                    .includes(searchPdfValue.toLocaleLowerCase())
                const matchesFilter =
                    !filterPdfValue.length ||
                    (entity.createdBy?.userId && filterPdfValue.includes(entity.createdBy.userId))
                const matches = matchesSearch && matchesFilter
                if (matches || filteredChildren.length > 0) {
                    return { ...data, children: filteredChildren }
                }
                return null
            })
            .filter((x): x is SidePanelPdfsData => x !== null)
    }
    const filteredList = filterList(sidePanelData)
    const compareFn = compareFunctionsBySortMode[sortPdfValue]
    const sortArr = (arr: SidePanelPdfsData[]) => {
        arr.sort((a, b) => {
            const entityA = entityMap[a.id]
            const entityB = entityMap[b.id]
            return compareFn(entityA, entityB)
        })
        arr.forEach((item) => {
            if (item.children) sortArr(item.children)
        })
    }
    sortArr(filteredList)
    return filteredList
}

export const useFlattenedFilteredAndSortedPdfSidePanelData = () => {
    const pdfs = useFilteredAndSortedPdfSidePanelData()
    const expandedLayerIds = useLocalStorageValue("sidePanelExpandedLayerIds")
    const flattenedPdfs = useMemo(() => {
        const result = [] as FlattenedSidePanelPdfsData[]
        const stack = [] as ({
            pdf: SidePanelPdfsData
        } & Pick<FlattenedSidePanelPdfsData, "depth" | "ancestors" | "isLastItemInParent">)[]
        for (let i = pdfs.length - 1; i >= 0; i--) {
            stack.push({
                pdf: pdfs[i],
                depth: 0,
                ancestors: [],
                isLastItemInParent: i === pdfs.length - 1,
            })
        }
        while (stack.length > 0) {
            const { pdf, depth, ancestors, isLastItemInParent } = stack.pop()!
            result.push({
                ...omit(pdf, ["children"]),
                depth,
                isLastItemInParent,
                directChildrenCount: pdf.children.length,
                ancestors,
            })
            if (pdf.children.length) {
                if (expandedLayerIds.includes(pdf.id)) {
                    for (let i = pdf.children.length - 1; i >= 0; i--) {
                        stack.push({
                            pdf: pdf.children[i],
                            depth: depth + 1,
                            ancestors: [...ancestors, { id: pdf.id, isLastItemInParent }],
                            isLastItemInParent: i === pdf.children.length - 1,
                        })
                    }
                }
            }
        }
        return result
    }, [expandedLayerIds, pdfs])
    return flattenedPdfs
}

export const usePdfSidePanelSortedEntities = () => {
    const pdfSidePanelData = useFilteredAndSortedPdfSidePanelData()
    const entityMap = usePdfSidePanelEntityMap()
    const getEntities = (sidePanelData: SidePanelPdfsData) => {
        const entity = entityMap[sidePanelData.id]
        entity.children = sidePanelData.children.map((child) => getEntities(child))
        return entity
    }
    return pdfSidePanelData.map((layer) => getEntities(layer))
}
